import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gold-500/20 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-gold-500" />
              </div>
              <div>
                <h1 className="font-display text-4xl">Privacy Policy</h1>
                <p className="text-slate-500">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-invert prose-gold max-w-none">
              <div className="bg-charcoal border border-slate-800 rounded-sm p-8 mb-8">
                <p className="text-slate-300 leading-relaxed">
                  At ExpertBase, we are committed to protecting your privacy and ensuring the security of your personal information. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
                </p>
              </div>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">1. Information We Collect</h2>
                <div className="space-y-4 text-slate-300">
                  <h3 className="text-lg font-semibold text-white">Personal Information</h3>
                  <p>When you register for an account, we collect:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Name and email address</li>
                    <li>Professional title and biography</li>
                    <li>Areas of expertise and industry experience</li>
                    <li>Profile photo (optional)</li>
                    <li>LinkedIn profile URL (optional)</li>
                    <li>Timezone and availability preferences</li>
                    <li>Payment information (processed securely via Stripe)</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6">Usage Information</h3>
                  <p>We automatically collect:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Device information and browser type</li>
                    <li>IP address and location data</li>
                    <li>Pages visited and actions taken on our platform</li>
                    <li>Session duration and interaction patterns</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">2. How We Use Your Information</h2>
                <div className="space-y-4 text-slate-300">
                  <p>We use the information we collect to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide, maintain, and improve our services</li>
                    <li>Match users with relevant experts based on their needs</li>
                    <li>Process transactions and send related information</li>
                    <li>Send promotional communications (with your consent)</li>
                    <li>Respond to your comments, questions, and requests</li>
                    <li>Monitor and analyze usage patterns and trends</li>
                    <li>Detect, investigate, and prevent fraudulent transactions</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">3. Information Sharing</h2>
                <div className="space-y-4 text-slate-300">
                  <p>We may share your information with:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Other Users:</strong> Your public profile information is visible to users browsing our platform. If you choose "Stay Anonymous," your identifiable information will be hidden.</li>
                    <li><strong>Service Providers:</strong> Third-party vendors who assist us in operating our platform (e.g., Stripe for payments, cloud hosting providers).</li>
                    <li><strong>Business Partners:</strong> For enterprise clients, limited information may be shared to facilitate team building and recruitment services.</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and the safety of others.</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">4. Data Security</h2>
                <div className="space-y-4 text-slate-300">
                  <p>We implement appropriate technical and organizational measures to protect your personal information, including:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security assessments and audits</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Secure payment processing through PCI-compliant providers</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">5. Your Rights</h2>
                <div className="space-y-4 text-slate-300">
                  <p>You have the right to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Access and receive a copy of your personal data</li>
                    <li>Rectify inaccurate or incomplete information</li>
                    <li>Request deletion of your personal data</li>
                    <li>Object to or restrict processing of your data</li>
                    <li>Data portability</li>
                    <li>Withdraw consent at any time</li>
                  </ul>
                  <p className="mt-4">To exercise these rights, please contact us at <a href="mailto:privacy@expertbase.ai" className="text-gold-500 hover:text-gold-400">privacy@expertbase.ai</a></p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">6. Cookies and Tracking</h2>
                <div className="space-y-4 text-slate-300">
                  <p>We use cookies and similar tracking technologies to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Remember your preferences and settings</li>
                    <li>Understand how you use our platform</li>
                    <li>Provide personalized content and recommendations</li>
                    <li>Analyze and improve our services</li>
                  </ul>
                  <p className="mt-4">You can manage your cookie preferences through your browser settings.</p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">7. International Transfers</h2>
                <div className="space-y-4 text-slate-300">
                  <p>
                    ExpertBase is headquartered in Dubai, UAE. Your information may be transferred to and processed in countries 
                    other than your country of residence. We ensure appropriate safeguards are in place for such transfers in 
                    compliance with applicable data protection laws.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="font-display text-2xl text-gold-500 mb-4">8. Contact Us</h2>
                <div className="space-y-4 text-slate-300">
                  <p>If you have any questions about this Privacy Policy, please contact us:</p>
                  <div className="bg-obsidian border border-slate-800 p-6 rounded-sm mt-4">
                    <p><strong>ExpertBase</strong></p>
                    <p>Dubai, UAE</p>
                    <p>Email: <a href="mailto:privacy@expertbase.ai" className="text-gold-500 hover:text-gold-400">privacy@expertbase.ai</a></p>
                  </div>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
